import React, { useState, useEffect, useCallback } from 'react';
import { ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { images, navItems, communityData, testimonialsData, aboutData } from '../data/mockData';

const FooterButterflyLogo = () => {
  const [imgLoaded, setImgLoaded] = useState(true);
  
  if (imgLoaded) {
    return (
      <img
        src={images.logo}
        alt="A Healing Touch"
        className="h-10 w-auto"
        onError={() => setImgLoaded(false)}
      />
    );
  }
  
  return (
    <svg className="h-10 w-auto" viewBox="0 0 80 70" fill="none">
      <path d="M40 35 C30 15, 5 5, 10 30 C12 40, 25 42, 40 35" fill="#C9A84C" opacity="0.8"/>
      <path d="M40 35 C50 15, 75 5, 70 30 C68 40, 55 42, 40 35" fill="#C9A84C" opacity="0.8"/>
      <path d="M40 35 C32 45, 15 55, 20 40 C22 36, 30 38, 40 35" fill="#7B4F9E" opacity="0.7"/>
      <path d="M40 35 C48 45, 65 55, 60 40 C58 36, 50 38, 40 35" fill="#7B4F9E" opacity="0.7"/>
      <line x1="40" y1="30" x2="40" y2="60" stroke="#4A154B" strokeWidth="1.5"/>
      <line x1="40" y1="30" x2="35" y2="20" stroke="#4A154B" strokeWidth="1"/>
      <line x1="40" y1="30" x2="45" y2="20" stroke="#4A154B" strokeWidth="1"/>
    </svg>
  );
};

const BottomSections = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const nextTestimonial = useCallback(() => {
    setCurrentTestimonial(
      (prev) => (prev + 1) % testimonialsData.testimonials.length
    );
  }, []);

  useEffect(() => {
    const interval = setInterval(nextTestimonial, 5000);
    return () => clearInterval(interval);
  }, [nextTestimonial]);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <>
      {/* Come Join Me */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Left - Intro */}
            <div
              className="relative overflow-hidden rounded-sm p-8"
              style={{
                background:
                  'linear-gradient(135deg, #D8BEE4 0%, #E8D5F0 50%, #D0B4E0 100%)',
              }}
            >
              <div
                className="absolute inset-0 opacity-20"
                style={{
                  background:
                    'radial-gradient(ellipse at 30% 80%, rgba(255,255,255,0.4) 0%, transparent 70%)',
                }}
              />
              <div className="relative z-10">
                <p className="font-seaweed text-[#C9A84C] text-3xl mb-1">
                  {communityData.scriptLabel}
                </p>
                <h2 className="font-playfair text-[#1a1a1a] text-2xl md:text-3xl font-bold mb-6 leading-tight">
                  {communityData.heading}
                </h2>
                <p className="font-open-sans text-[#555] text-sm leading-relaxed">
                  {communityData.intro}
                </p>
              </div>
            </div>

            {/* Community Cards */}
            {communityData.cards.map((card, i) => (
              <div
                key={i}
                className="bg-white border border-gray-100 rounded-sm p-8 shadow-sm hover:shadow-lg transition-shadow duration-300"
              >
                <h3 className="font-open-sans text-[#1a1a1a] text-sm tracking-[0.2em] font-bold mb-2">
                  {card.title}
                </h3>
                <div className="w-full h-px bg-gray-200 mb-6" />
                {card.paragraphs.map((p, j) => (
                  <p
                    key={j}
                    className="font-open-sans text-[#888] text-sm leading-relaxed mb-4"
                  >
                    {p}
                  </p>
                ))}
                <a
                  href="#"
                  className="inline-flex items-center gap-3 text-[#C9A84C] font-open-sans text-xs tracking-[0.2em] uppercase hover:text-[#B89740] transition-colors duration-300 mt-2"
                >
                  {card.linkText}
                  <span className="text-lg">—</span>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 px-4 bg-[#F3ECF8]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-seaweed text-[#C9A84C] text-3xl md:text-4xl mb-1">
            {testimonialsData.scriptLabel}
          </p>
          <h2 className="font-playfair text-[#1a1a1a] text-3xl md:text-4xl lg:text-5xl font-bold mb-12 leading-tight">
            {testimonialsData.heading}
          </h2>

          {/* Testimonial Carousel */}
          <div className="relative min-h-[200px]">
            {testimonialsData.testimonials.map((t, i) => (
              <div
                key={i}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  i === currentTestimonial
                    ? 'opacity-100'
                    : 'opacity-0 pointer-events-none'
                }`}
              >
                <p className="font-playfair text-[#555] text-lg md:text-xl italic leading-relaxed mb-6 max-w-2xl mx-auto">
                  {t.text}
                </p>
                <p className="font-open-sans text-[#C9A84C] text-sm tracking-wider">
                  - {t.name}
                </p>
              </div>
            ))}
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonialsData.testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentTestimonial(i)}
                className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                  i === currentTestimonial
                    ? 'bg-[#C9A84C] scale-110'
                    : 'bg-[#C9A84C]/30 hover:bg-[#C9A84C]/50'
                }`}
                aria-label={`Testimonial ${i + 1}`}
              />
            ))}
          </div>

          <a
            href="#"
            className="inline-flex items-center gap-3 mt-8 text-[#C9A84C] font-open-sans text-xs tracking-[0.2em] uppercase hover:text-[#B89740] transition-colors duration-300"
          >
            {testimonialsData.buttonText}
          </a>
        </div>
      </section>

      {/* About Roberta */}
      <section
        className="py-16 md:py-24 px-4 bg-white bg-no-repeat bg-left bg-contain"
        style={{
          backgroundImage:
            "url('https://customer-assets.emergentagent.com/job_gold-text-refresh/artifacts/bl814bu0_flower-madala-bg2.jpg')",
        }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 rounded-full border-4 border-[#8B5EA5]/20 scale-110" />
                <div className="absolute inset-0 rounded-full border-2 border-[#8B5EA5]/10 scale-[1.2]" />
                <img
                  src={images.roberta}
                  alt="Roberta Millard"
                  className="w-72 h-72 md:w-[400px] md:h-[400px] rounded-full object-cover border-4 border-[#8B5EA5]/40 shadow-xl"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1600334129128-685c5582fd35?w=400&h=400&fit=crop';
                  }}
                />
              </div>
            </div>

            {/* Text */}
            <div>
              <p className="font-seaweed text-[#C9A84C] text-3xl md:text-4xl mb-1">
                {aboutData.scriptLabel}
              </p>
              <h2 className="font-playfair text-[#1a1a1a] text-3xl md:text-4xl lg:text-5xl font-bold mb-8 leading-tight">
                {aboutData.heading}
              </h2>

              <p className="font-open-sans text-[#888] text-base leading-relaxed mb-4">
                is a well-rounded being who has gathered much knowledge and wisdom
                over the years. As a master at her craft, her{' '}
                <span className="font-playfair text-[#333] text-xl">journey</span>{' '}
                has been quite the colorful walk. Her adventures through childhood,
                adulthood, motherhood, and BEing a healer have provided the{' '}
                <span className="font-seaweed text-[#9B59B6] text-2xl">
                  opportunity
                </span>{' '}
                to learn many sides of the human experience.
              </p>

              <p className="font-open-sans text-[#888] text-base leading-relaxed mb-4">
                She has had an up close and personal view of going from a victim to
                a survivor to blooming into the{' '}
                <span className="font-playfair text-[#2980B9] text-xl">
                  thriver
                </span>{' '}
                that she is. She can truly understand without judgment; deep
                depression, abuse, addiction, cancer, and many other life
                experiences that make each of us unique.
              </p>

              <p className="font-open-sans text-[#888] text-base leading-relaxed mb-8">
                She looks to every{' '}
                <span className="font-playfair text-[#E74C3C] text-2xl">
                  experience
                </span>{' '}
                as being 'rich' as they allow us to really know what others can
                only read about. In her own experiences, she has chosen to use
                these as her master teacher. She believes the more difficult, the
                groovier the learning is.
              </p>

              <a
                href="#"
                className="inline-flex items-center gap-3 text-[#C9A84C] font-open-sans text-xs tracking-[0.2em] uppercase hover:text-[#B89740] transition-colors duration-300"
              >
                {aboutData.buttonText}
                <span className="text-lg">—</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="hidden md:grid grid-cols-[1fr_auto_1fr] items-center gap-6">
            <div className="flex items-center justify-end gap-6">
              {navItems.left.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  className="text-[#5B2C6F] hover:text-[#C9A84C] transition-colors duration-300 text-sm font-open-sans tracking-[0.12em] uppercase"
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <Link to="/">
              <FooterButterflyLogo />
            </Link>
            <div className="flex items-center justify-start gap-6">
              {navItems.right.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  className="text-[#5B2C6F] hover:text-[#C9A84C] transition-colors duration-300 text-sm font-open-sans tracking-[0.12em] uppercase"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
          {/* Mobile footer */}
          <div className="md:hidden flex flex-wrap items-center justify-center gap-4">
            <Link to="/" className="w-full flex justify-center mb-4">
              <FooterButterflyLogo />
            </Link>
            {[...navItems.left, ...navItems.right].map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className="text-[#5B2C6F] hover:text-[#C9A84C] transition-colors duration-300 text-xs font-open-sans tracking-[0.12em] uppercase"
              >
                {item.label}
              </Link>
            ))}
          </div>
          <p className="text-center text-[#999] text-xs mt-6 font-open-sans">
            © {new Date().getFullYear()} A Healing Touch Pain Release. All rights
            reserved.
          </p>
        </div>
      </footer>

      {/* Scroll to Top */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-6 right-6 z-50 w-12 h-12 bg-[#C9A84C] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#B89740] transition-colors duration-300 ${
          showScrollTop
            ? 'opacity-100 visible'
            : 'opacity-0 invisible pointer-events-none'
        }`}
        aria-label="Scroll to top"
      >
        <ChevronUp size={24} />
      </button>
    </>
  );
};

export default BottomSections;
